import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { OrganisationContact } from "../../organisation/objects/organisationContact";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_COMMERCIAL_INFORMATION, EMPTY_COMMERCIAL_INFORMATION_LIST_CONFIGURATION, EMPTY_COMMERCIAL_INFORMATION_LIST_CRITERIA } from "../constant/commercialInformationConstant";
import { CommercialInformation } from "../objects/commercialInformation";
import { CommercialInformationListCriteria } from "../objects/commercialInformationListCriteria";
import { CommercialInformationState } from "../objects/commercialInformationtate";
import { setCommercialInformationPreference } from "../utils/setCommercialInformationPreference";
import { Column } from "../objects/column";


const initialState: CommercialInformationState = {
    commercial_information_list: [],
    commercial_information_list_configuration: EMPTY_COMMERCIAL_INFORMATION_LIST_CONFIGURATION,
    commercial_information_list_loading: false,
    commercial_information_list_count: 0,
    commercial_information_list_criteria: EMPTY_COMMERCIAL_INFORMATION_LIST_CRITERIA,
    commercial_information: EMPTY_COMMERCIAL_INFORMATION,
    commercial_information_contacts: [],
    commercial_information_contacts_loading: false,
    commercial_information_contact: null,
    first_preference_loaded: false
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const addCommercialInformationContactReducer = (state: CommercialInformationState, action: PayloadAction<OrganisationContact>): CommercialInformationState => ({
    ...state, commercial_information_contacts: [action.payload, ...state.commercial_information_contacts]
});

const updateCommercialInformationContactReducer = (state: CommercialInformationState, action: PayloadAction<OrganisationContact>): CommercialInformationState => ({
    ...state, commercial_information_contacts: state.commercial_information_contacts.map((contact) => contact.id === action.payload.id ? action.payload : contact)
});

const updateCommercialInformationContactsReducer = (state: CommercialInformationState, action: PayloadAction<OrganisationContact[]>): CommercialInformationState => ({
    ...state, commercial_information_contacts: action.payload
});

const deleteCommercialInformationContactReducer = (state: CommercialInformationState, action: PayloadAction<number>): CommercialInformationState => ({
    ...state, commercial_information_contacts: state.commercial_information_contacts.filter((contact) => contact.id !== action.payload)
});

const addCommercialInformationListConfigurationPreferenceReducer = (state: CommercialInformationState, action: PayloadAction<Preferences[]>): CommercialInformationState => ({
    ...state, commercial_information_list_configuration: { ...state.commercial_information_list_configuration, preferences: [...state.commercial_information_list_configuration.preferences, ...action.payload] }
});

const setCommercialInformationListConfigurationPreferenceReducer = (state: CommercialInformationState, action: PayloadAction<Preferences[]>): CommercialInformationState => ({
    ...state, commercial_information_list_configuration: { ...state.commercial_information_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: CommercialInformationState, action: PayloadAction<Preferences[]>): CommercialInformationState => {
    const { preferences, columns, order_by, order } = setCommercialInformationPreference(action.payload, state.commercial_information_list_configuration.columns);
    return {
        ...state,
        commercial_information_list_configuration: { ...state.commercial_information_list_configuration, preferences, columns },
        commercial_information_list_criteria: { ...state.commercial_information_list_criteria, order, order_by },
        first_preference_loaded: true
    };
};

const toggleColumnReducer = (state: CommercialInformationState, action: PayloadAction<number>): CommercialInformationState => {
    const columns = cloneDeep([...state.commercial_information_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, commercial_information_list_configuration: { ...state.commercial_information_list_configuration, columns } };
};

const updateCommercialInformationListReducer = (state: CommercialInformationState, action: PayloadAction<CommercialInformation[]>): CommercialInformationState => ({
    ...state, commercial_information_list: action.payload
});
const updateCommercialInformationListCriteriaReducer = (state: CommercialInformationState, action: PayloadAction<CommercialInformationListCriteria>): CommercialInformationState => ({
    ...state, commercial_information_list_criteria: action.payload
});
const updateCommercialInformationReducer = (state: CommercialInformationState, action: PayloadAction<CommercialInformation>): CommercialInformationState => ({
    ...state, commercial_information: action.payload
});
const updateCommercialInformationListLoadingReducer = (state: CommercialInformationState, action: PayloadAction<boolean>): CommercialInformationState => ({
    ...state, commercial_information_list_loading: action.payload
});
const updateCommercialInformationListCountReducer = (state: CommercialInformationState, action: PayloadAction<number>): CommercialInformationState => ({
    ...state, commercial_information_list_count: action.payload
});
const setColumnsReducer = (state: CommercialInformationState, action: PayloadAction<Column[]>): CommercialInformationState => ({
    ...state, commercial_information_list_configuration: { ...state.commercial_information_list_configuration, columns: action.payload },
});

export const commercial_information = createSlice({
    name: 'commercial_information',
    initialState,
    reducers: {
        addCommercialInformationContact: addCommercialInformationContactReducer,
        updateCommercialInformationContact: updateCommercialInformationContactReducer,
        updateCommercialInformationContacts: updateCommercialInformationContactsReducer,
        deleteCommercialInformationContact: deleteCommercialInformationContactReducer,
        addCommercialInformationListConfigurationPreference: addCommercialInformationListConfigurationPreferenceReducer,
        setCommercialInformationListConfigurationPreference: setCommercialInformationListConfigurationPreferenceReducer,
        toggleColumn: toggleColumnReducer,
        updateCommercialInformationList: updateCommercialInformationListReducer,
        updateCommercialInformationListCriteria: updateCommercialInformationListCriteriaReducer,
        updateCommercialInformation: updateCommercialInformationReducer,
        updateCommercialInformationListLoading: updateCommercialInformationListLoadingReducer,
        updateCommercialInformationListCount: updateCommercialInformationListCountReducer,
        setColumns: setColumnsReducer
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    addCommercialInformationContact,
    updateCommercialInformationContact,
    updateCommercialInformationContacts,
    deleteCommercialInformationContact,
    addCommercialInformationListConfigurationPreference,
    setCommercialInformationListConfigurationPreference,
    setColumns,
    toggleColumn,
    updateCommercialInformationList,
    updateCommercialInformationListCriteria,
    updateCommercialInformation,
    updateCommercialInformationListLoading,
    updateCommercialInformationListCount
} = commercial_information.actions;